/**
 * PopupErrorBoundary - Error boundary for popup windows
 *
 * Catches unhandled errors in popup components and displays a user-friendly
 * error page with option to close the window. Reports errors to Sentry.
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Scope } from '@sentry/react';
import { Button } from '@ais-ui/ui-core';
import { AlertTriangle } from 'lucide-react';
import { closePopup } from '@ais-ui/popups';

interface PopupErrorBoundaryProps {
  children: ReactNode;
}

interface PopupErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class PopupErrorBoundary extends Component<
  PopupErrorBoundaryProps,
  PopupErrorBoundaryState
> {
  constructor(props: PopupErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PopupErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope((scope: Scope) => {
      scope.setExtras({
        componentStack: errorInfo.componentStack,
        ...errorInfo,
      });
      scope.setTag('context', 'popup-error-boundary');
      scope.setTag('window.type', 'popup');
      Sentry.captureException(error);
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen p-8 bg-background text-foreground">
          <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground text-center mb-6 max-w-md">
            An unexpected error occurred in this popup window. Please close and
            try again.
          </p>
          {this.state.error && (
            <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md mb-6 max-w-md overflow-auto">
              <code>{this.state.error.message}</code>
            </div>
          )}
          <Button onClick={closePopup}>Close Window</Button>
        </div>
      );
    }

    return this.props.children;
  }
}
